import {PAGINATION_DEBUG_FLAGS} from '../../../../util/paginationTelemetry';
import {
  PAGE_BREAK_SELECTOR,
  PAGE_BREAK_PAGE_INDEX_ATTR,
  PAGE_BREAK_TOP_SELECTOR,
  PAGE_BREAK_WITH_PAGE_INDEX_SELECTOR,
  PAGE_INDEX_EPSILON_PX,
} from './pagination.constants';
import {getWidowOrphanMinLines} from './htmlDom.utils';
import {classifyLineVisibilityAtCut} from './lineGeometry.utils';
import {isParagraphLikeElement} from './tagClassification.utils';
import {resolveBoundaryClassificationTarget} from './boundaryClassification.utils';

// W/O-specific style/attribute contracts are intentionally local to this file.
/** Inline style var for per-page WO footer carry; must match `DocumentStyles.css`. */
export const WO_ADJUST_CSS_VAR = '--wo-adjust';
const WO_CLASSNAME = 'page-break-wo-adjust';
const WO_SELECTOR = `.${WO_CLASSNAME}`;
const WIDOW_ORPHAN_EPSILON = 1;
const EDITOR_WO_BREAKER_BOOST_ATTR = 'data-wo-breaker-footer-boost';
export const EDITOR_WO_PAGE_FOOTER_BOOST_ATTR = 'data-wo-footer-boost';

/**
 * Widow/Orphan visual adjustment engine (editor-side).
 *
 * ⚠️ FRAGILE AREA — READ BEFORE EDITING
 * - Small arithmetic or ordering changes here can shift page boundaries and
 *   cause editor/print parity regressions that only appear in long documents.
 * - This file intentionally prefers deterministic convergence over "perfect"
 *   local split optimality.
 * - If you change carry math, boundary refresh conditions, or pass stopping
 *   rules, validate with the W/O QA checklist in:
 *   `pagination/readme/pagination-widow-orphan-control.md`
 *
 * Mental model:
 * 1) We detect nodes that cross a page boundary.
 * 2) We classify how many text lines are above/below that boundary.
 * 3) If a split violates widow/orphan rules, we "carry" content by increasing
 *    per-page footer boost (shrinking writable area for that page).
 * 4) We apply boosts to both page margin and page-break overlay DOM.
 * 5) We iterate in a bounded cascade because each applied boost can move later
 *    boundaries and change downstream classifications.
 *
 * Important constraints:
 * - This file intentionally favors stability over perfect optimality.
 * - Most guards are anti-jitter protections to avoid oscillating boundaries.
 * - All geometry is read from live DOM; there is no persistent cross-run cache.
 */

/**
 * Keep numeric comparisons stable across zoom/sub-pixel DOM math.
 * Purity: pure.
 */
export const round2 = (value) => Math.round(value * 100) / 100;

/**
 * Classify whether a split needs widow/orphan carry handling.
 * Purity: pure.
 */
export const getWidowOrphanSplitState = (visibility, widowOrphanMinLines) => {
  // Normalized counts (fall back to 0 when geometry is unavailable).
  const aboveLineCount = visibility?.aboveLineCount ?? 0;
  const belowLineCount = visibility?.belowLineCount ?? 0;
  const totalLineCount = visibility?.totalLineCount ?? 0;
  const hasFiniteLineCounts =
    Number.isFinite(aboveLineCount) && Number.isFinite(belowLineCount);
  const hasFiniteTotalLineCount = Number.isFinite(totalLineCount);
  const hasMeasuredTextLinesForWidowOrphan =
    aboveLineCount + belowLineCount > 0;
  // "Below short" means next-page side has fewer lines than required minimum.
  const belowShortForWidowOrphan =
    Boolean(visibility?.below) &&
    hasFiniteLineCounts &&
    belowLineCount < widowOrphanMinLines;
  // "Above short" means current-page side has fewer lines than required minimum.
  const aboveShortForWidowOrphan =
    Boolean(visibility?.above) &&
    hasFiniteLineCounts &&
    aboveLineCount < widowOrphanMinLines;
  // Special case: geometry says there are measurable lines, but none count as
  // "above". This is treated as an invalid split and carried as a whole block.
  const shouldCarryFromZeroAboveVisible =
    hasMeasuredTextLinesForWidowOrphan &&
    !visibility?.above &&
    Boolean(visibility?.below) &&
    hasFiniteLineCounts &&
    hasFiniteTotalLineCount &&
    totalLineCount >= widowOrphanMinLines * 2 &&
    aboveLineCount === 0 &&
    belowLineCount > 0;
  // Standard widow/orphan invalid split: text on both sides but one side short.
  const shouldCarryFromWidowOrphan =
    hasMeasuredTextLinesForWidowOrphan &&
    Boolean(visibility?.above) &&
    Boolean(visibility?.below) &&
    (belowShortForWidowOrphan || aboveShortForWidowOrphan);

  return {
    shouldCarryFromZeroAboveVisible,
    shouldCarryFromWidowOrphan,
  };
};

/**
 * Merge page soft caps by taking the stricter bound.
 * Purity: pure.
 */
export const mergeSoftCap = (existingCap, nextCap) =>
  Number.isFinite(existingCap) ? Math.min(existingCap, nextCap) : nextCap;

/**
 * Commit a requested boost only when it meaningfully increases.
 * Purity: pure.
 */
export const getCommittedBoost = (
  prevBoost,
  requestedBoost,
  minDelta = 0.5
) => {
  if (!(requestedBoost > 0)) return null;
  if (requestedBoost < prevBoost + minDelta) return null;
  return Math.max(prevBoost, requestedBoost);
};

/**
 * Compute carry for below-short splits (N|1, N|0, etc).
 * Purity: pure.
 */
export const computeBelowShortCarryBoost = ({
  cutHeight,
  prevBoost,
  visibility,
  widowOrphanMinLines,
}) => {
  // How many lines must move from above -> below to satisfy minimum below lines.
  const shortageToFill = Math.max(
    0,
    widowOrphanMinLines - visibility.belowLineCount
  );
  // True if moving `shortageToFill` lines still leaves enough lines above.
  const canSatisfyBothSides =
    visibility.aboveLineCount - shortageToFill >= widowOrphanMinLines;
  // Preferred above-line count after rebalancing (or 0 when full carry needed).
  const targetAboveCount = canSatisfyBothSides
    ? visibility.aboveLineCount - shortageToFill
    : 0;
  // Candidate cut at the bottom of the target last-above line.
  const targetCutFromLine =
    targetAboveCount > 0
      ? visibility.aboveLineBottoms?.[targetAboveCount - 1]
      : null;
  // Next line below target; used to place midpoint cut for stability.
  const nextLineBottom = visibility.aboveLineBottoms?.[targetAboveCount];
  const hasFiniteNextLineBottom = Number.isFinite(nextLineBottom);
  const hasFiniteTargetCutFromLine = Number.isFinite(targetCutFromLine);
  const hasExactTarget =
    hasFiniteTargetCutFromLine &&
    targetCutFromLine >= 0 &&
    targetCutFromLine <= cutHeight;

  let footerBoostPx = 0;
  if (hasExactTarget && canSatisfyBothSides) {
    // Best case: both sides can be satisfied by a partial shift.
    let desiredCut = targetCutFromLine;
    const lineGap =
      hasFiniteNextLineBottom && hasFiniteTargetCutFromLine
        ? nextLineBottom - targetCutFromLine
        : null;
    const hasFiniteLineGap = Number.isFinite(lineGap);
    if (
      hasFiniteNextLineBottom &&
      nextLineBottom > targetCutFromLine &&
      hasFiniteLineGap
    ) {
      // Place the break between adjacent lines to avoid threshold jitter
      // and avoid over-carrying past the next valid split.
      desiredCut = (targetCutFromLine + nextLineBottom) / 2;
    } else {
      desiredCut = targetCutFromLine + WIDOW_ORPHAN_EPSILON;
    }
    desiredCut = Math.min(cutHeight, Math.max(0, desiredCut));
    footerBoostPx = Math.max(0, round2(Math.max(0, cutHeight - desiredCut)));
    if (hasFiniteLineGap && lineGap > 0) {
      const maxDeltaForShortage = Math.max(
        1,
        lineGap * Math.max(1, shortageToFill)
      );
      const cappedBoost = Math.min(
        cutHeight,
        round2(prevBoost + maxDeltaForShortage)
      );
      if (footerBoostPx > cappedBoost + 0.01) {
        footerBoostPx = cappedBoost;
      }
    }
  } else if (!canSatisfyBothSides) {
    // Cannot satisfy both minimums by partial shift (e.g. 2|1 at min=2).
    // Cannot satisfy both minimums by partial line rebalance (e.g. 2|1 at min=2),
    // so this block must move fully to the next page. Use the minimum carry
    // that places the boundary just above line 1, instead of over-pushing by
    // full cutHeight (which can drag the adjacent block too).
    const firstAboveLineBottom = visibility.aboveLineBottoms?.[0];
    const hasFiniteFirstAboveLineBottom = Number.isFinite(firstAboveLineBottom);
    if (hasFiniteFirstAboveLineBottom) {
      const targetCut = Math.max(
        0,
        firstAboveLineBottom - WIDOW_ORPHAN_EPSILON
      );
      footerBoostPx = Math.max(0, round2(Math.max(0, cutHeight - targetCut)));
    } else {
      footerBoostPx = Math.max(0, round2(cutHeight));
    }
  } else {
    // Fallback proportional shift when exact line geometry target is missing.
    const proportionalBoost =
      (cutHeight * shortageToFill) / visibility.aboveLineCount;
    footerBoostPx = Math.max(
      0,
      round2(Math.min(cutHeight, Math.max(0, proportionalBoost)))
    );
  }

  if (footerBoostPx <= prevBoost + 0.5) {
    // Ensure forward progress to avoid stalls from tiny/sub-pixel deltas.
    const inferredLineStep =
      hasFiniteNextLineBottom && hasFiniteTargetCutFromLine
        ? Math.max(1, (nextLineBottom - targetCutFromLine) / 2)
        : Math.max(1, cutHeight / Math.max(1, visibility.aboveLineCount * 2));
    footerBoostPx = Math.min(cutHeight, round2(prevBoost + inferredLineStep));
  }
  return footerBoostPx;
};

/**
 * Compute carry for above-short splits (1|N).
 * Purity: pure.
 */
export const computeAboveShortCarryBoost = ({
  cutHeight,
  prevBoost,
  visibility,
}) => {
  // For 1|N style splits, move boundary above the first currently-visible line.
  const firstAboveLineBottom = visibility.aboveLineBottoms?.[0] ?? null;
  const hasFiniteFirstAboveLineBottom = Number.isFinite(firstAboveLineBottom);
  let footerBoostPx = hasFiniteFirstAboveLineBottom
    ? Math.max(
        0,
        round2(
          Math.max(
            0,
            cutHeight - Math.max(0, firstAboveLineBottom - WIDOW_ORPHAN_EPSILON)
          )
        )
      )
    : Math.max(0, round2(cutHeight));
  if (footerBoostPx <= prevBoost + 0.5) {
    // Same anti-stall rule as below-short path.
    const inferredCarryStep = hasFiniteFirstAboveLineBottom
      ? Math.max(1, firstAboveLineBottom / 2)
      : Math.max(1, cutHeight / 2);
    footerBoostPx = Math.max(
      footerBoostPx,
      round2(prevBoost + inferredCarryStep)
    );
  }
  return footerBoostPx;
};

/**
 * Single entry-point for invalid-split carry sizing.
 * Purity: pure.
 */
export const computeCarryBoostForInvalidSplit = ({
  shouldCarryFromFullOverflow,
  shouldCarryFromZeroAboveVisible,
  overflowPx,
  cutHeight,
  prevBoost,
  visibility,
  widowOrphanMinLines,
}) => {
  // Decision order matters: these branches are mutually exclusive priorities.
  // 1) Full overflow (node starts below boundary) -> carry exact overflow.
  if (shouldCarryFromFullOverflow) {
    return Math.max(0, round2(overflowPx));
  }
  // 2) Zero-above-visible anomaly -> one-time full cut carry, no ratchet.
  if (shouldCarryFromZeroAboveVisible) {
    // Once a zero-above-visible carry has already been committed, do not keep
    // ratcheting this page's boost higher; repeated escalation can pull
    // adjacent nodes across the boundary in later passes.
    if (prevBoost > 0) {
      return Math.max(0, round2(prevBoost));
    }
    return Math.max(0, round2(cutHeight));
  }

  const aboveShort = visibility.aboveLineCount < widowOrphanMinLines;
  const belowShort = visibility.belowLineCount < widowOrphanMinLines;

  // 3) Below short only (N|1, N|0, ...): targeted below-short rebalance.
  if (belowShort && !aboveShort && visibility.aboveLineCount > 0) {
    return computeBelowShortCarryBoost({
      cutHeight,
      prevBoost,
      visibility,
      widowOrphanMinLines,
    });
  }

  // 4) Above short only (1|N): targeted above-short rebalance.
  if (aboveShort && !belowShort) {
    return computeAboveShortCarryBoost({cutHeight, prevBoost, visibility});
  }

  // 5) Both short (1|1 etc): minimal full-carry behavior.
  if (belowShort && aboveShort && visibility.aboveLineCount > 0) {
    const isUnsplittableMinLineBlock =
      Number.isFinite(visibility.totalLineCount) &&
      visibility.totalLineCount <= widowOrphanMinLines;
    const isAlreadyFullyCarriedFromCurrentPage =
      visibility.aboveLineCount === 0 || !visibility.above;
    if (
      isUnsplittableMinLineBlock &&
      prevBoost > 0 &&
      isAlreadyFullyCarriedFromCurrentPage
    ) {
      // For unsplittable 2-line blocks, freeze boost only after the block is
      // already fully carried. If it is still visible as 1|1, allow another
      // carry step so we do not leave an invalid split.
      return Math.max(0, round2(prevBoost));
    }
    // Both sides short (e.g. 1|1) still need a full carry, but using
    // cutHeight over-pushes downstream boundaries. Reuse the minimal full-carry
    // path from below-short sizing so we move just enough to clear line 1.
    return computeBelowShortCarryBoost({
      cutHeight,
      prevBoost,
      visibility,
      widowOrphanMinLines,
    });
  }

  // 6) Conservative fallback.
  return Math.max(0, round2(cutHeight));
};

/**
 * Build all page-boundary crossings for one candidate element.
 * Why: tall blocks can cross multiple page boundaries in one pass, and WO
 * adjustments must evaluate each crossing independently to avoid blind spots.
 * Purity: impure (reads element/client rect geometry).
 */
const getBoundaryCandidates = (
  el,
  sectionTop,
  boundaryEntries,
  pageIndexEpsilonPx
) => {
  const rect = el?.getBoundingClientRect?.();
  if (!rect || rect.height <= 0) return [];
  const relTop = rect.top - sectionTop;
  const relBottom = rect.bottom - sectionTop;
  return boundaryEntries
    .filter(
      (entry) =>
        entry.boundaryY > relTop + pageIndexEpsilonPx &&
        entry.boundaryY < relBottom - pageIndexEpsilonPx
    )
    .map((entry) => ({
      relTop,
      relBottom,
      pageIdx: entry.pageIdx,
      boundaryY: entry.boundaryY,
    }));
};

/**
 * Classify line visibility using shared line-geometry classifier.
 * Purity: impure (depends on DOM measurement/cache reads/writes).
 */
const classifyLineVisibilityWithCounts = (
  el,
  cutHeight,
  lineGeometryCache = null
) => {
  // Thin wrapper to keep naming explicit at call sites.
  return classifyLineVisibilityAtCut(el, cutHeight, lineGeometryCache);
};

/**
 * Toggle WO debug root class from the current debug-flags snapshot.
 * Reads `debugFlags.pagination.woAdjust` when available.
 * Purity: impure (mutates `document.documentElement.classList`).
 */
export const syncWoAdjustDebugRootClass = (debugFlags = null) => {
  if (!document?.documentElement) return;
  const woAdjustDebug = PAGINATION_DEBUG_FLAGS.woAdjust;
  if (!woAdjustDebug?.rootClassName) return;
  const isEnabled = Boolean(debugFlags?.pagination?.woAdjust);
  document.documentElement.classList.toggle(
    woAdjustDebug.rootClassName,
    Boolean(isEnabled)
  );
};

/**
 * Apply widow/orphan visual controls to editor/page-break DOM.
 * Purity: impure (reads/writes layout and attributes in live DOM).
 *
 * Runtime flow:
 * 1) Read page-break boundaries from live DOM.
 * 2) For each straddling paragraph-like node, classify line visibility.
 * 3) Compute/commit per-page boost values (carry or rebalance).
 * 4) Write boosts to CSS custom property `--wo-adjust` on `.page` and
 *    `.page-break`, then let CSS own spacing math.
 * 5) Re-measure and iterate until no progress (or pass cap reached).
 */
export const applyEditorWidowOrphanVisualControls = ({
  editorRef,
  editorContent,
  pagesRef,
  neededPages,
  getPageMargin,
  // Fallback used when CSS var is temporarily missing/invalid.
  // Caller passes the same baseline used by snapshot production.
  widowOrphanMinLinesFallback = 1,
  pageIndexEpsilonPx = PAGE_INDEX_EPSILON_PX,
}) => {
  if (!editorRef?.current || !editorContent || !pagesRef?.current) return;

  // We only evaluate top-level editor children for WO adjustments. This mirrors
  // how pagination/planner treats block-level nodes as boundary candidates.
  const allNodes = Array.from(editorContent.children || []);
  const pageEls = Array.from(pagesRef.current.querySelectorAll('.page'));
  /**
   * Collect sorted boundary entries from live page-break DOM.
   * Purity: impure (reads DOM/query + layout geometry).
   */
  const getBoundaryEntries = () => {
    const sectionRect = editorRef.current?.getBoundingClientRect?.();
    if (!sectionRect) return [];
    const entries = Array.from(
      pagesRef.current?.querySelectorAll?.(
        PAGE_BREAK_WITH_PAGE_INDEX_SELECTOR
      ) || []
    )
      .map((breakerEl) => {
        const pageIdx = parseInt(
          breakerEl.getAttribute(PAGE_BREAK_PAGE_INDEX_ATTR) || '',
          10
        );
        const topLayer = breakerEl.querySelector(PAGE_BREAK_TOP_SELECTOR);
        const topRect = topLayer?.getBoundingClientRect?.();
        if (!Number.isInteger(pageIdx) || !topRect) return null;
        return {
          pageIdx,
          boundaryY: round2(topRect.top - sectionRect.top),
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.pageIdx - b.pageIdx);
    return entries;
  };

  // Feature switch from CSS variable.
  const widowOrphanMinLines = getWidowOrphanMinLines(
    widowOrphanMinLinesFallback
  );
  if (widowOrphanMinLines <= 1) {
    // Widow/orphan disabled: restore base page footer-space boundaries.
    // We clear only WO-owned state (`--wo-adjust` + boost attributes) so
    // base header/footer sizing remains controlled by existing page CSS vars.
    pageEls.forEach((pageEl) => {
      if (pageEl.getAttribute(EDITOR_WO_PAGE_FOOTER_BOOST_ATTR) === '1') {
        pageEl.style.removeProperty(WO_ADJUST_CSS_VAR);
        pageEl.removeAttribute(EDITOR_WO_PAGE_FOOTER_BOOST_ATTR);
      }
    });
    pagesRef.current
      ?.querySelectorAll?.(
        `${PAGE_BREAK_SELECTOR}[${EDITOR_WO_BREAKER_BOOST_ATTR}="1"]`
      )
      ?.forEach?.((breakerEl) => {
        const topLayer = breakerEl.querySelector(PAGE_BREAK_TOP_SELECTOR);
        const woAdjustLayer = breakerEl.querySelector(WO_SELECTOR);
        if (topLayer) {
          topLayer.style.height = '';
        }
        if (woAdjustLayer) {
          breakerEl.style.removeProperty(WO_ADJUST_CSS_VAR);
        }
        breakerEl.removeAttribute(EDITOR_WO_BREAKER_BOOST_ATTR);
      });
    return;
  }

  const sectionRect = editorRef.current.getBoundingClientRect();
  // WO math runs in rendered-space coordinates (getBoundingClientRect), but
  // `--wo-adjust` participates in layout formulas that are authored in base
  // page CSS px. Convert at the DOM write/read boundary to keep zoom stable.
  const sectionStyles = window.getComputedStyle(editorRef.current);
  const pageWidth =
    parseFloat(sectionStyles.getPropertyValue('--page-width')) || 816;
  const contentRect = editorContent.getBoundingClientRect?.() || null;
  const sectionScale =
    Number.isFinite(contentRect?.width) &&
    contentRect.width > 0 &&
    Number.isFinite(pageWidth) &&
    pageWidth > 0
      ? contentRect.width / pageWidth
      : 1;
  const toCssPx = (renderedPx) =>
    sectionScale > 0 ? renderedPx / sectionScale : renderedPx;
  const toRenderedPx = (cssPx) =>
    sectionScale > 0 ? cssPx * sectionScale : cssPx;
  // Page-level carry/boost values accumulated for this invocation.
  const pageFooterBoostPxByPageIdx = new Map();
  // Per-pass text geometry cache (rebuilt each pass after DOM changes).
  let lineGeometryCache = new WeakMap();

  /**
   * Synchronize per-page/footer boosts onto page + page-break DOM layers.
   * Purity: impure (mutates DOM styles/attributes, reads computed styles).
   */
  const applyBoostsToDom = (boostsByPageIdx) => {
    // Synchronize computed boost values onto:
    // 1) `.page --wo-adjust` -> drives writable-area offset in CSS margin formula.
    // 2) `.page-break --wo-adjust` -> drives WO layer height for boundary visuals.
    //
    // Why this split:
    // - `.page` value influences actual layout and next boundary placement.
    // - `.page-break` value keeps overlay/debug layer in sync with that layout.
    let changedPageCount = 0;
    let changedBreakerCount = 0;
    const breakerByPageIdx = new Map();
    Array.from(
      pagesRef.current?.querySelectorAll?.(
        PAGE_BREAK_WITH_PAGE_INDEX_SELECTOR
      ) || []
    ).forEach((breakerEl) => {
      const idx = parseInt(
        breakerEl.getAttribute(PAGE_BREAK_PAGE_INDEX_ATTR) || '',
        10
      );
      if (Number.isInteger(idx)) breakerByPageIdx.set(idx, breakerEl);
    });

    pageEls.forEach((pageEl, idx) => {
      const pageIdx = Math.max(0, idx);
      const footerBoostRenderedPx = boostsByPageIdx.get(pageIdx) || 0;
      const footerBoostCssPx = round2(toCssPx(footerBoostRenderedPx));
      // Use inline style var read (not computed style) so we compare exactly
      // what this engine wrote previously on the page element.
      const currentWoAdjustCssPx =
        parseFloat(pageEl.style.getPropertyValue(WO_ADJUST_CSS_VAR) || '0') ||
        0;
      const currentWoAdjustRenderedPx = toRenderedPx(currentWoAdjustCssPx);
      const hasBoost =
        pageEl.getAttribute(EDITOR_WO_PAGE_FOOTER_BOOST_ATTR) === '1';
      if (footerBoostRenderedPx > 0) {
        if (
          !hasBoost ||
          Math.abs(currentWoAdjustRenderedPx - footerBoostRenderedPx) > 0.5
        ) {
          pageEl.style.setProperty(WO_ADJUST_CSS_VAR, `${footerBoostCssPx}px`);
          pageEl.setAttribute(EDITOR_WO_PAGE_FOOTER_BOOST_ATTR, '1');
          changedPageCount += 1;
        }
      } else if (hasBoost) {
        pageEl.style.removeProperty(WO_ADJUST_CSS_VAR);
        pageEl.removeAttribute(EDITOR_WO_PAGE_FOOTER_BOOST_ATTR);
        changedPageCount += 1;
      }

      const breakerEl = breakerByPageIdx.get(pageIdx);
      if (breakerEl) {
        const topLayer = breakerEl.querySelector(PAGE_BREAK_TOP_SELECTOR);
        const woAdjustLayer = ensureWoAdjustLayer(breakerEl);
        const hasBreakerBoost =
          breakerEl.getAttribute(EDITOR_WO_BREAKER_BOOST_ATTR) === '1';
        if (footerBoostRenderedPx > 0) {
          // Read computed WO layer height because breaker-level var may come
          // from inline style or stylesheet cascade.
          const currentWoAdjustCssHeight =
            parseFloat(getComputedStyle(woAdjustLayer).height || '0') || 0;
          const currentWoAdjustRenderedHeight = toRenderedPx(
            currentWoAdjustCssHeight
          );
          if (
            !hasBreakerBoost ||
            Math.abs(currentWoAdjustRenderedHeight - footerBoostRenderedPx) >
              0.5
          ) {
            if (topLayer) {
              topLayer.style.height = '';
            }
            // Single source of truth for breaker WO spacing.
            breakerEl.style.setProperty(
              WO_ADJUST_CSS_VAR,
              `${footerBoostCssPx}px`
            );
            breakerEl.setAttribute(EDITOR_WO_BREAKER_BOOST_ATTR, '1');
            changedBreakerCount += 1;
          }
        } else if (hasBreakerBoost) {
          if (topLayer) topLayer.style.height = '';
          breakerEl.style.removeProperty(WO_ADJUST_CSS_VAR);
          breakerEl.removeAttribute(EDITOR_WO_BREAKER_BOOST_ATTR);
          changedBreakerCount += 1;
        }
      }
    });
    return {changedPageCount, changedBreakerCount};
  };

  // Re-measure boundaries from the live DOM after each apply. A pure numeric
  // cascade with fixed node rects diverges for full_overflow (boost grows
  // every pass), which collapsed page margins and shoved content down.
  let cascadePasses = 0;
  const maxCascadePasses = Math.max(1, Math.min(neededPages + 1, 6));
  // Soft cap history carried across passes to prevent over-pushing on pages
  // that already have safe valid splits.
  const persistentPageSoftCapByPageIdx = new Map();
  while (cascadePasses < maxCascadePasses) {
    cascadePasses += 1;
    // Geometry can change after DOM carry commits/restarts; keep cache per pass.
    lineGeometryCache = new WeakMap();
    let boundaryEntries = getBoundaryEntries();
    // Pass-local soft caps derived from currently valid splits.
    const pageBoostSoftCapByPageIdx = new Map();

    // PASS A: collect caps from currently valid splits so invalid split handling
    // cannot escalate boosts beyond what keeps known-good splits safe.
    for (const el of allNodes) {
      if (!isParagraphLikeElement(el)) continue;
      const candidates = getBoundaryCandidates(
        el,
        sectionRect.top,
        boundaryEntries,
        pageIndexEpsilonPx
      );
      for (const candidate of candidates) {
        const {relTop, relBottom, pageIdx, boundaryY} = candidate;
        if (relBottom <= boundaryY + pageIndexEpsilonPx) continue;

        // Shared with snapshot producer: list-boundary classification resolves
        // to the deepest crossing text block when available.
        const {targetEl, targetCutHeight} = resolveBoundaryClassificationTarget(
          el,
          sectionRect.top + boundaryY
        );
        const effectiveCutHeight = targetCutHeight;
        // PASS A cap derivation must use the same effective target as PASS B, or
        // soft caps can be based on container geometry while carry uses leaf text.
        const visibility = classifyLineVisibilityWithCounts(
          targetEl,
          targetCutHeight,
          lineGeometryCache
        );
        const hasFiniteVisibleLineCounts =
          Number.isFinite(visibility.aboveLineCount) &&
          Number.isFinite(visibility.belowLineCount);
        const {shouldCarryFromZeroAboveVisible, shouldCarryFromWidowOrphan} =
          getWidowOrphanSplitState(visibility, widowOrphanMinLines);
        const shouldCarryFromFullOverflow =
          relTop >= boundaryY - pageIndexEpsilonPx;

        if (
          !shouldCarryFromFullOverflow &&
          !shouldCarryFromWidowOrphan &&
          !shouldCarryFromZeroAboveVisible &&
          visibility.above &&
          visibility.below &&
          hasFiniteVisibleLineCounts &&
          visibility.aboveLineCount >= widowOrphanMinLines &&
          visibility.belowLineCount >= widowOrphanMinLines
        ) {
          const currentBoost = pageFooterBoostPxByPageIdx.get(pageIdx) || 0;
          const keepAboveLineBottom =
            visibility.aboveLineBottoms?.[visibility.aboveLineCount - 1];
          const hasFiniteKeepAboveLineBottom =
            Number.isFinite(keepAboveLineBottom);
          if (hasFiniteKeepAboveLineBottom) {
            const boostCapForNode =
              currentBoost +
              Math.max(0, effectiveCutHeight - keepAboveLineBottom);
            const existingCap = pageBoostSoftCapByPageIdx.get(pageIdx);
            const nextCap = mergeSoftCap(existingCap, boostCapForNode);
            pageBoostSoftCapByPageIdx.set(pageIdx, nextCap);
            const priorPersistentCap =
              persistentPageSoftCapByPageIdx.get(pageIdx);
            const mergedPersistentCap = mergeSoftCap(
              priorPersistentCap,
              nextCap
            );
            persistentPageSoftCapByPageIdx.set(pageIdx, mergedPersistentCap);
          }
        }
      }
    }
    let passAddedBoost = false;
    let didIntraPassBoundaryRefresh = false;

    // PASS B: apply carry/rebalance decisions for invalid and valid splits.
    for (const el of allNodes) {
      if (!isParagraphLikeElement(el)) continue;
      const candidates = getBoundaryCandidates(
        el,
        sectionRect.top,
        boundaryEntries,
        pageIndexEpsilonPx
      );
      for (const candidate of candidates) {
        const {relTop, relBottom, pageIdx, boundaryY} = candidate;
        if (relBottom <= boundaryY + pageIndexEpsilonPx) {
          continue;
        }

        // Shared with snapshot producer: list-boundary classification resolves
        // to the deepest crossing text block when available.
        const {targetEl, targetCutHeight} = resolveBoundaryClassificationTarget(
          el,
          sectionRect.top + boundaryY
        );
        const effectiveCutHeight = targetCutHeight;
        // Overflow remains container-relative (layout overflow on page), while
        // line split logic below uses target-relative `targetCutHeight`.
        const overflowPx = Math.max(0, relBottom - boundaryY);
        const visibility = classifyLineVisibilityWithCounts(
          targetEl,
          targetCutHeight,
          lineGeometryCache
        );
        const hasFiniteVisibleLineCounts =
          Number.isFinite(visibility.aboveLineCount) &&
          Number.isFinite(visibility.belowLineCount);
        const {shouldCarryFromZeroAboveVisible, shouldCarryFromWidowOrphan} =
          getWidowOrphanSplitState(visibility, widowOrphanMinLines);
        const shouldCarryFromFullOverflow =
          relTop >= boundaryY - pageIndexEpsilonPx;
        const shouldCarryWhole =
          shouldCarryFromFullOverflow ||
          shouldCarryFromZeroAboveVisible ||
          shouldCarryFromWidowOrphan;
        if (shouldCarryWhole) {
          // Invalid split path: compute required carry and commit if meaningful.
          const prevBoost = pageFooterBoostPxByPageIdx.get(pageIdx) || 0;
          let footerBoostPx = computeCarryBoostForInvalidSplit({
            shouldCarryFromFullOverflow,
            shouldCarryFromZeroAboveVisible,
            overflowPx,
            // Must match the element used for line bottoms/counts.
            cutHeight: targetCutHeight,
            prevBoost,
            visibility,
            widowOrphanMinLines,
          });
          const pageSoftCap =
            pageBoostSoftCapByPageIdx.get(pageIdx) ??
            persistentPageSoftCapByPageIdx.get(pageIdx);
          const hasInvalidWidowOrphanSplit =
            shouldCarryFromWidowOrphan || shouldCarryFromZeroAboveVisible;
          const isTwoOneCandidate =
            visibility.above &&
            visibility.below &&
            visibility.aboveLineCount === widowOrphanMinLines &&
            visibility.belowLineCount === widowOrphanMinLines - 1;
          // Only cap non-invalid carry candidates; invalid splits must be fixed.
          if (
            Number.isFinite(pageSoftCap) &&
            footerBoostPx > pageSoftCap &&
            pageSoftCap >= 0 &&
            !hasInvalidWidowOrphanSplit
          ) {
            footerBoostPx = pageSoftCap;
          }
          const committedBoost = getCommittedBoost(
            prevBoost,
            footerBoostPx,
            0.5
          );
          if (committedBoost != null) {
            pageFooterBoostPxByPageIdx.set(pageIdx, committedBoost);
            passAddedBoost = true;
            // Why refresh mid-pass:
            // freshly committed carry on page N can immediately move the measured
            // boundary for page N+1. Reusing stale geometry in the same pass can
            // over-carry downstream nodes, so we refresh boundaries right away
            // for the highest-risk candidates.
            const shouldRefreshBoundariesAfterCarry =
              isTwoOneCandidate ||
              shouldCarryFromWidowOrphan ||
              shouldCarryFromZeroAboveVisible;
            if (shouldRefreshBoundariesAfterCarry) {
              applyBoostsToDom(pageFooterBoostPxByPageIdx);
              boundaryEntries = getBoundaryEntries();
              didIntraPassBoundaryRefresh = true;
            }
          }
        } else if (
          visibility.above &&
          visibility.below &&
          hasFiniteVisibleLineCounts
        ) {
          // This branch is the "non-invalid split" tuning path.
          // At this point both sides already satisfy minimum widow/orphan lines, so
          // we do not need to carry whole content blocks. Instead we try a gentle
          // *downward rebalance*: reduce previously-added footer boost if doing so
          // moves the boundary closer to a preferred split without causing churn.
          const totalLineCount = visibility.totalLineCount || 0;
          // Preferred split target:
          // - keep at least `widowOrphanMinLines` above
          // - keep at least `widowOrphanMinLines` below
          // If there is extra room, bias toward moving one more line above.
          const preferredAboveCount = Math.max(
            widowOrphanMinLines,
            totalLineCount - widowOrphanMinLines
          );
          const currentBoost = pageFooterBoostPxByPageIdx.get(pageIdx) || 0;
          const firstBelowLineBottom = visibility.belowLineBottoms?.[0];
          const aboveLineBottoms = visibility.aboveLineBottoms || [];
          const localLineStep = Math.max(
            1,
            aboveLineBottoms.length >= 2
              ? Math.abs(
                  aboveLineBottoms[aboveLineBottoms.length - 1] -
                    aboveLineBottoms[aboveLineBottoms.length - 2]
                )
              : effectiveCutHeight / Math.max(1, visibility.totalLineCount || 1)
          );
          // `preferredCut` is the line-bottom position where we would ideally place
          // the boundary to meet `preferredAboveCount`.
          const preferredCut =
            preferredAboveCount <= visibility.aboveLineCount
              ? visibility.aboveLineBottoms?.[preferredAboveCount - 1]
              : preferredAboveCount === visibility.aboveLineCount + 1 &&
                  Number.isFinite(firstBelowLineBottom)
                ? firstBelowLineBottom
                : visibility.allLineBottoms?.[preferredAboveCount - 1];
          const hasFinitePreferredCut = Number.isFinite(preferredCut);
          // How far we would need to move the cut downward to reach the preferred
          // split. Positive value means "need less boost than we currently have."
          const desiredCutIncrease = hasFinitePreferredCut
            ? Math.max(0, preferredCut - effectiveCutHeight)
            : 0;
          // Convert that desired cut movement into a lower footer boost.
          const rebalanceBoost = hasFinitePreferredCut
            ? Math.max(
                0,
                Math.round((currentBoost - desiredCutIncrease) * 100) / 100
              )
            : currentBoost;
          // Guardrails to prevent oscillation/jitter:
          // - only rebalance when target is near the current cut
          // - require a meaningful delta (> 0.5px)
          // - never increase boost in this branch (downward only)
          const isNearPreferredCut =
            desiredCutIncrease <= Math.max(2, localLineStep * 1.25);
          const canApplyDownwardRebalance =
            preferredAboveCount > visibility.aboveLineCount &&
            currentBoost > 0 &&
            hasFinitePreferredCut &&
            desiredCutIncrease > 0.5 &&
            isNearPreferredCut &&
            rebalanceBoost <= currentBoost - 0.5;
          if (canApplyDownwardRebalance) {
            pageFooterBoostPxByPageIdx.set(pageIdx, rebalanceBoost);
            passAddedBoost = true;
          }
        }
      }
    }

    // If we did not intra-pass refresh, apply once at pass end.
    if (!didIntraPassBoundaryRefresh) {
      applyBoostsToDom(pageFooterBoostPxByPageIdx);
    }
    // Stop when a full pass makes no additional progress.
    // Why: boosts are monotonic (increase in invalid path, decrease in bounded
    // rebalance path), so "no change" is a stable fixed point for this section.
    if (!passAddedBoost) {
      break;
    }
  }
};

/**
 * Ensure a page-break WO adjustment layer element exists.
 * Purity: impure (creates/inserts DOM nodes).
 */
export const ensureWoAdjustLayer = (breakerEl) => {
  if (!breakerEl) return null;
  let woAdjustLayer = breakerEl.querySelector(WO_SELECTOR);
  if (!woAdjustLayer) {
    woAdjustLayer = document.createElement('div');
    woAdjustLayer.classList.add(WO_CLASSNAME);
    const topLayer = breakerEl.querySelector(PAGE_BREAK_TOP_SELECTOR);
    if (topLayer) {
      breakerEl.insertBefore(woAdjustLayer, topLayer);
    } else {
      breakerEl.prepend(woAdjustLayer);
    }
  }
  return woAdjustLayer;
};
